import { FC, useEffect, useState } from 'react'
import { Button, Grid, styled, useMediaQuery, useTheme, Box, Autocomplete, Alert, List, ListItem } from '@mui/material'
import { EmployeeEntity, SectionEntity, TicketItemWithDailyEntity } from 'api/generated'
import AppModal from 'components/AppModal'
import { H2, H3, Small } from 'components/Typography'
import { employeesApi, paymentSectionsApi, ticketApi, turnoverApi } from 'api'
import toast, { Toaster } from 'react-hot-toast'
import * as Yup from 'yup'
import { useFormik } from 'formik'
import { format, isAfter, isSameDay } from 'date-fns'
import CalendarInput from 'components/input-fields/CalendarInput'
import AppTextField from 'components/input-fields/AppTextField'
import { ignoreUTC } from 'utils/utils'
import { useSeason } from 'contexts/SeasonContext'
import { useTranslation } from 'react-i18next'
import { AxiosResponse } from 'axios'

interface AddTicketModalProps {
  open: boolean
  onClose: () => void
  section?: SectionEntity
  isOwner: boolean
  fetchDataCallBack: () => void;
}

const StyledAppModal = styled(AppModal)(({ theme }) => ({
  width: useMediaQuery(theme.breakpoints.down('sm')) ? '100%' : 650,
  maxHeight: useMediaQuery(theme.breakpoints.down('sm')) ? '80%' : 600,
  overflowY: 'auto',
}))

const currencyFormatter = (value: number) =>
  new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(value)

const AddTicketModal: FC<AddTicketModalProps> = ({ open, onClose, section, isOwner, fetchDataCallBack }) => {
  const theme = useTheme()

  const [date, setDate] = useState(ignoreUTC(new Date()))
  const [isDateValid, setIsDateValid] = useState(true)
  const [ticketData, setTicketData] = useState<TicketItemWithDailyEntity[]>([])

  const [extraValue, setExtraValue] = useState(0)
  const [cannotEdit, setCannotEdit] = useState(false)
  const [sectionsToCompile, setSectionsToCompile] = useState<SectionEntity[]>([]);
  const { seasonId } = useSeason();
  const [employees, setEmployees] = useState<EmployeeEntity[]>([]);
  const [selectedEmployee, setSelectedEmployee] = useState<EmployeeEntity>();


  const { t } = useTranslation();


  const fieldValidationSchema = Yup.array().of(
    Yup.object({
      lastTicketValue: Yup.number(),
      value: Yup.number().required(t('value.notValid')).min(Yup.ref('lastTicketValue'), t('tickets.minNumber')),
    })
  )

  const formik = useFormik({
    initialValues: ticketData.map(t => {

      const isThereItemToday = t?.DailyTicketing[0] && isSameDay(ignoreUTC(new Date(t.DailyTicketing[0].date)), date);


      const lastEntry = isThereItemToday ? t.DailyTicketing[1] : t?.DailyTicketing[0];

      let lastTicketValue = 0;

      let lastTicketDate: Date | undefined;

      // last entry must be after the last reset
      if (lastEntry && (isAfter(new Date(lastEntry.date), new Date(t.resetAtDate))
        || isSameDay(new Date(lastEntry.date), new Date(t.resetAtDate)))) {
        lastTicketValue = (lastEntry.value)
        lastTicketDate = (new Date(lastEntry.date))
      }
      else if (!lastEntry) {
        lastTicketValue = 0;
        lastTicketDate = undefined;
      }
      else {
        lastTicketValue = (t.resetAtValue)
        lastTicketDate = (new Date(t.resetAtDate))
      }
      const dailyTurnoverSum = (t?.dailyTurnoverSum || 0)
      const dailyTurnoverSumToExclude = (t?.dailyTurnoverSumToExclude || 0)

      return {
        value: isThereItemToday ? t.DailyTicketing.at(0)?.value ?? 0 : lastTicketValue,
        itemId: t.id,
        dailyTicketId: t.DailyTicketing.at(0)?.id,
        employeeId: t.DailyTicketing.at(0)?.employeeId,
        name: t.name,
        price: t.price,
        isThereItemToday,
        lastTicketValue,
        lastTicketDate,
        sectionIdToExclude: t.paymentSectionToExcludeId,
        dailyTurnoverSum,
        dailyTurnoverSumToExclude,
      }

    }),
    enableReinitialize: true,
    validationSchema: fieldValidationSchema,
    onSubmit: async (values) => {
      try {
        if (!section) {
          onClose()
          return
        }

        const promises: Array<Promise<any>> = [];

        for (const selectedItem of values) {

          if (selectedItem.isThereItemToday && selectedItem.dailyTicketId) {
            const body = {
              value: +selectedItem.value,
              employeeId: isOwner ? selectedEmployee?.id : undefined,
            }
            promises.push(ticketApi.updateDaily(selectedItem.dailyTicketId.toString(), body));

          }

          else {
            const body = {
              date: ignoreUTC(date).toISOString(),
              itemId: selectedItem.itemId,
              value: +selectedItem.value,
              employeeId: isOwner ? selectedEmployee?.id : undefined,
            }
            promises.push(ticketApi.createDaily(seasonId, body));
          }
        }

        await Promise.all(promises);
        fetchDataCallBack();
        toast.success(t('alerts.success'))
        onClose()
      } catch {
        toast.error(t('common.errors.default'))
      }
    },
  })


  const fetchCompiledTurnvovers = async () => {

    if (!section)
      return;

    const sectionIds: number[] = [];

    sectionIds.push(section?.id);

    for (const t of ticketData) {
      if (t.paymentSectionToExcludeId && !sectionIds.find(id => id === t.paymentSectionToExcludeId))
        sectionIds.push(t.paymentSectionToExcludeId);
    }

    let sectionNotCompiled: SectionEntity[] = [];

    const sectionPromises: Array<Promise<AxiosResponse<SectionEntity, any>>> = [];

    for (const id of sectionIds) {
      const isThereItem = (await turnoverApi.itemOfDay(date.setUTCHours(9), seasonId, id)).data;
      if (!isThereItem) {
        if (id === section.id) {
          sectionNotCompiled.push(section);
        }
        else {
          sectionPromises.push(paymentSectionsApi.findOne(id, seasonId));
        }
      }
    }

    const respones = (await Promise.all(sectionPromises)).map(r => r.data);


    sectionNotCompiled = sectionNotCompiled.concat(respones);

    setSectionsToCompile(sectionNotCompiled);

  }

  useEffect(() => {
    fetchCompiledTurnvovers();
  }, [ticketData]);

  useEffect(() => {
    if (!open) {
      setDate(ignoreUTC(new Date()))
      setIsDateValid(true)
      setTicketData([])
      /* setDailyTurnoverSum(0)
      setDailyTurnoverSumToExclude(0) */
      setExtraValue(0)
      setCannotEdit(false)
      setEmployees([])
      setSelectedEmployee(undefined)
      /*   setSectionsTurnoverTotal(0)
         setSectionsEcludeTurnoverTotal(0) */
      setSectionsToCompile([])

      formik.resetForm()
    }
  }, [open])

  useEffect(() => {
    if (open && section) {
      ticketApi.getLastValues(date.toISOString()).then(({ data }) => {
        const filtered = data.filter(
          (d: any) =>
            d.paymentSectionId === section.id ||
            d.paymentSectionToExcludeId === section.id
        ).sort((a, b) => {
          return a.name.toLocaleLowerCase().localeCompare(b.name.toLocaleLowerCase());
        })
        setTicketData(filtered)
      })
    };

    if (isOwner) {
      employeesApi
        .findAllDepchief(seasonId, true, section?.departmentId)
        .then(({ data }) => {
          setEmployees(data);
        });
    } else {
      employeesApi.getMySelf().then(({ data }) => {
        setSelectedEmployee(data);
      });
    }
  }, [open, section, date])



  useEffect(() => {

    let total = 0;



    const visitedSectionsIds: number[] = [];

    let dailyTurnoversSumToExclude = 0;

    const dailyTurnoverSum = ticketData[0]?.dailyTurnoverSum ?? 0;

    for (const ticket of formik.values) {

      const val = ticket.value;
      const p = ticket.price;

      const ticketTotal = (val - ticket.lastTicketValue) * p;

      total += ticketTotal;

      if (!visitedSectionsIds.includes(ticket.sectionIdToExclude ?? 0)) {
        visitedSectionsIds.push(ticket.sectionIdToExclude ?? 0);
        dailyTurnoversSumToExclude += ticket.dailyTurnoverSumToExclude;
      }

    }

    setExtraValue(total - dailyTurnoverSum - dailyTurnoversSumToExclude);

  }, [
    formik.values
  ])


  useEffect(() => {
    setCannotEdit(!!formik.values.find(t => t.isThereItemToday));
    // this solution assumes that all tickets are inserted by the same person on the selected day
    if (formik.values.find(t => t.isThereItemToday))
      setSelectedEmployee(employees.find(e => e.id === formik.values.find(t => t.isThereItemToday)?.employeeId));
  }, [formik.values])

  return (
    <StyledAppModal
      open={open}
      handleClose={() => {
        onClose()
      }}
      alignContent={'center'}
      alignItems={'center'}
    >
      <Toaster position='top-center' reverseOrder={false} />
      <Grid container alignItems={'center'}>
        <Grid item xs={12}>
          <H2 mb={2}>{t('add.ticketing')}</H2>
        </Grid>
      </Grid>

      <CalendarInput
        sx={{ marginBottom: 2 }}
        format='dd.MM.yyyy'
        value={date}
        onChange={(newValue) => {
          if (newValue && newValue instanceof Date && !isNaN(+newValue)) {
            setDate(ignoreUTC(new Date(newValue)))
            setIsDateValid(true)
          } else setIsDateValid(false)
        }}
        disableFuture
        slotProps={{
          textField: {
            helperText: !isDateValid && t('date.error'),
            error: !isDateValid,
          },
        }}
      />

      {
        sectionsToCompile.length > 0 ? (

          <>
            <Alert color='warning'>
              {
                t('ticekts.sectionMissing')
              }

              <List>
                {
                  sectionsToCompile.map((s) => {
                    return (
                      <ListItem>
                        {s.name}
                      </ListItem>
                    )
                  })
                }
              </List>
            </Alert>

            <Grid container justifyContent={'flex-end'} marginTop={4}>
              <Grid item xs={12}>
                <Button
                  fullWidth
                  variant='contained'
                  color='error'
                  onClick={onClose}
                >
                  {t('dialog.close')}
                </Button>

              </Grid>
            </Grid>
          </>
        ) :

          (
            <>
              {(!isOwner && cannotEdit) && (
                <Small color={theme.palette.warning.main} marginBottom={4}>
                  {t('tickets.isThereTicket')}
                </Small>
              )}
              {isOwner && (
                <Autocomplete
                  sx={{ marginBottom: 2 }}
                  value={selectedEmployee ?? null}
                  options={employees}
                  disabled={!isOwner || employees.length < 1}
                  getOptionLabel={(employee) => {
                    return employee.firstName + ' ' + employee.lastName;
                  }}
                  renderInput={(params) => (
                    <AppTextField
                      {...params}
                      label={t('employee.select')}
                      inputProps={{
                        ...params.inputProps,
                      }}
                      error={!selectedEmployee && isOwner}
                      helperText={
                        !selectedEmployee && isOwner && t('turnover.employeeError')
                      }
                    />
                  )}
                  onChange={(_event, value) => {
                    if (value) {
                      setSelectedEmployee(value);
                    }
                  }}
                />
              )}



              <form onSubmit={formik.handleSubmit}>
                <Grid container spacing={2} >
                  {
                    formik.values.map((selectedItem, index) => {
                      return (
                        <Grid item container xs={12} sm={6}>

                          {selectedItem && (
                            <Box mb={2}>
                              <H3>
                                {selectedItem.name} - {t('ticekt.price')}: {' '}
                                {currencyFormatter(ticketData.find(t => t.id === selectedItem.itemId)?.price ?? 0)}
                              </H3>

                              <Grid item xs={12} my={2}>
                                <AppTextField
                                  label={t('ticket.currentValue')}
                                  name={'value' + selectedItem.itemId}
                                  type='number'
                                  value={selectedItem.value}
                                  onChange={(e) => {
                                    formik.setFieldValue(`[${index}].value`, e.target.value)
                                  }}
                                  error={!!formik.errors[index]?.value}
                                  helperText={formik.errors[index]?.value}
                                  disabled={!isOwner && selectedItem.isThereItemToday}
                                  fullWidth
                                />
                              </Grid>

                              <Small>
                                {t('ticket.lastSold')}: {' '}
                                {!selectedItem.isThereItemToday ? selectedItem.value : selectedItem.lastTicketValue}
                                {

                                  ' ' + (selectedItem.lastTicketDate?.toLocaleDateString('it-IT') ?? '')// ? ` (${format(lastTicketDate, 'dd/MM/yyyy')})` : ''
                                }

                              </Small>
                              <br />
                              <Small>
                                {t('ticket.resetAt')}: {format(new Date(ticketData.find(t => t.id === selectedItem.itemId)?.resetAtDate ?? 0), 'dd/MM/yyyy')}
                              </Small>
                            </Box>
                          )}


                        </Grid>
                      )
                    })
                  }
                  <Grid item xs={12}>
                    <Box
                      sx={{
                        width: '100%',
                        minHeight: 54,
                        display: 'flex',
                        alignItems: 'center',
                      }}
                    >
                      <H3>
                        {t('ticket.extra')}: {' '}
                        {currencyFormatter(extraValue)}
                      </H3>
                    </Box>
                  </Grid>
                  <Grid item container justifyContent={'flex-end'} gap={2} marginTop={4}>
                    <Button
                      fullWidth
                      variant='contained'
                      color='error'
                      onClick={onClose}
                    >
                      {t('cancel')}
                    </Button>
                    <Button
                      fullWidth
                      type='submit'
                      variant='contained'
                      disabled={(!isOwner && cannotEdit) || (isOwner && !selectedEmployee)}
                    >
                      {t('save')}
                    </Button>
                  </Grid>
                </Grid>

              </form>
            </>
          )

      }




    </StyledAppModal>
  )
}

export default AddTicketModal
